import { admin } from '../api';
import { getCurrentUser } from '../auth';
import { createPageHeader } from '../components/form';
import { showToast } from '../components/toast';
import type { User } from '../types';

function roleBadge(role: string): string {
  const colours: Record<string, string> = {
    admin: 'badge-red',
    owner: 'badge-green',
    associate: 'badge-blue',
  };
  return `<span class="badge ${colours[role] ?? 'badge-gray'}">${role}</span>`;
}

function renderTable(users: User[]): string {
  if (!users.length) {
    return '<p class="text-sm text-gray-500">No users found.</p>';
  }
  const rows = users.map(u => `
    <tr class="hover:bg-gray-50">
      <td class="px-4 py-3 text-sm font-medium text-gray-900">${u.username}</td>
      <td class="px-4 py-3 text-sm text-gray-600">${u.name || '—'}</td>
      <td class="px-4 py-3 text-sm text-gray-600">${u.email || '—'}</td>
      <td class="px-4 py-3 text-sm">${roleBadge(u.role)}</td>
      <td class="px-4 py-3 text-sm text-gray-400">${u.invited_by ? `by ${u.invited_by}` : '—'}</td>
      <td class="px-4 py-3 text-sm text-gray-400">${u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
    </tr>
  `).join('');

  return `
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invited By</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">${rows}</tbody>
      </table>
    </div>
  `;
}

export async function adminUsersPage(): Promise<HTMLElement> {
  const currentUser = getCurrentUser();

  const page = document.createElement('div');
  page.appendChild(createPageHeader('User Management'));

  if (currentUser?.role !== 'admin') {
    page.innerHTML += `<div class="card"><p class="text-red-600">Access denied. Admin role required.</p></div>`;
    return page;
  }

  // Load users
  const usersCard = document.createElement('div');
  usersCard.className = 'card mb-6';
  usersCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">All Users</h3><p class="text-sm text-gray-400">Loading...</p>`;
  page.appendChild(usersCard);

  // Create user form card
  const formCard = document.createElement('div');
  formCard.className = 'card';
  formCard.innerHTML = `
    <h3 class="font-semibold text-gray-900 mb-1">Create New User</h3>
    <p class="text-sm text-gray-500 mb-4">Account is created immediately and credentials are emailed to the user.</p>
    <form id="create-user-form" class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="label">Username <span class="text-red-500">*</span></label>
        <input name="username" type="text" class="input-field" required autocomplete="off" placeholder="e.g. jane_smith">
      </div>
      <div>
        <label class="label">Password <span class="text-red-500">*</span></label>
        <input name="password" type="text" class="input-field" required autocomplete="new-password" placeholder="Set a strong password">
      </div>
      <div>
        <label class="label">Full Name</label>
        <input name="name" type="text" class="input-field" placeholder="Jane Smith">
      </div>
      <div>
        <label class="label">Email <span class="text-red-500">*</span></label>
        <input name="email" type="email" class="input-field" required placeholder="jane@example.com">
      </div>
      <div>
        <label class="label">Phone</label>
        <input name="phone" type="tel" class="input-field" placeholder="+61400000000">
      </div>
      <div>
        <label class="label">Role</label>
        <select name="role" class="input-field">
          <option value="owner">Owner</option>
          <option value="associate">Associate</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      <div class="md:col-span-2 flex gap-3 items-center">
        <button type="submit" class="btn-primary" id="create-user-btn">Create User</button>
        <span id="create-user-status" class="text-sm text-gray-500 hidden"></span>
      </div>
    </form>
  `;
  page.appendChild(formCard);

  // Fetch and display users
  const loadUsers = async () => {
    try {
      const users = await admin.listUsers();
      usersCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">All Users <span class="text-sm text-gray-400">(${users.length})</span></h3>` + renderTable(users);
    } catch (err) {
      usersCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">All Users</h3><p class="text-red-500 text-sm">Failed to load users: ${err instanceof Error ? err.message : String(err)}</p>`;
    }
  };

  await loadUsers();

  // Wire up form
  const form = formCard.querySelector<HTMLFormElement>('#create-user-form')!;
  const btn = formCard.querySelector<HTMLButtonElement>('#create-user-btn')!;
  const status = formCard.querySelector<HTMLElement>('#create-user-status')!;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;

    btn.disabled = true;
    btn.textContent = 'Creating...';
    status.className = 'text-sm text-gray-500';
    status.textContent = '';
    status.classList.remove('hidden');

    try {
      const newUser = await admin.createUser({
        username: data.username,
        password: data.password,
        email: data.email,
        phone: data.phone || '',
        name: data.name || '',
        role: data.role,
      });
      showToast(`User "${newUser.username}" created successfully. Welcome email sent.`, 'success');
      form.reset();
      await loadUsers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Failed to create user: ${msg}`, 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Create User';
      status.classList.add('hidden');
    }
  });

  return page;
}
